import { supabase } from "@/integrations/supabase/client";
import { HabitType } from "@/types";

export interface SupabaseHabit {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  type: 'positive' | 'negative' | 'neutral';
  difficulty: 'trivial' | 'easy' | 'medium' | 'hard';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  value?: number;
  frequency?: string;
  points?: number;
  streak?: number;
  color?: string;
  icon?: string;
  tags?: string[];
  habit_value?: number;
  daily_completions?: number;
  positive?: boolean;
  archived?: boolean;
  last_completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface SupabaseDaily {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  difficulty: 'trivial' | 'easy' | 'medium' | 'hard';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  points?: number;
  streak?: number;
  color?: string;
  icon?: string;
  tags?: string[];
  completed_today?: boolean;
  last_completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface SupabaseTodo {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  difficulty: 'trivial' | 'easy' | 'medium' | 'hard';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  points?: number;
  color?: string;
  icon?: string;
  tags?: string[];
  completed?: boolean;
  due_date?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export const loadHabitsFromSupabase = async (userId: string): Promise<HabitType[]> => {
  try {
    console.log('🔍 Loading habits for user:', userId);
    
    const { data: habits, error } = await supabase
      .from('habits')
      .select('*')
      .eq('user_id', userId)
      .eq('archived', false)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Failed to load habits:', error);
      return [];
    }

    console.log('📈 Raw habits query returned:', habits?.length || 0, 'habits');
    console.log('📋 Habit names:', habits?.map(h => h.name));

    const transformedHabits = transformSupabaseHabitsToLocal(habits as SupabaseHabit[] || []);
    console.log('✅ Transformed habits:', transformedHabits.length, 'habits');

    return transformedHabits;
  } catch (error) {
    console.error('❌ Error loading habits from Supabase:', error);
    return [];
  }
};

export const loadDailiesFromSupabase = async (userId: string): Promise<HabitType[]> => {
  try {
    console.log('🔍 Loading dailies for user:', userId);
    
    const { data: dailies, error } = await supabase
      .from('dailies')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Failed to load dailies:', error);
      return [];
    }

    console.log('📈 Raw dailies query returned:', dailies?.length || 0, 'dailies');

    return transformSupabaseDailiesToLocal(dailies as SupabaseDaily[] || []);
  } catch (error) {
    console.error('❌ Error loading dailies from Supabase:', error);
    return [];
  }
};

export const loadTodosFromSupabase = async (userId: string): Promise<HabitType[]> => {
  try {
    console.log('🔍 Loading todos for user:', userId);
    
    const { data: todos, error } = await supabase
      .from('todos')
      .select('*')
      .eq('user_id', userId)
      .eq('completed', false)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Failed to load todos:', error);
      return [];
    }

    console.log('📈 Raw todos query returned:', todos?.length || 0, 'todos');

    return transformSupabaseTodosToLocal(todos as SupabaseTodo[] || []);
  } catch (error) {
    console.error('❌ Error loading todos from Supabase:', error);
    return [];
  }
};

const transformSupabaseHabitsToLocal = (supabaseHabits: SupabaseHabit[]): HabitType[] => {
  return supabaseHabits.map(habit => ({
    id: habit.id,
    userId: habit.user_id,
    name: habit.name,
    description: habit.description || '',
    type: habit.type as 'daily' | 'habit' | 'todo',
    difficulty: habit.difficulty,
    priority: habit.priority || 'medium',
    points: habit.points || 1,
    streak: habit.streak || 0,
    lastCompleted: habit.last_completed_at ? new Date(habit.last_completed_at) : undefined,
    completedToday: false, // Habits don't have completedToday, always false
    tags: habit.tags || [],
    icon: habit.icon,
    color: habit.color || '#3B82F6',
    positive: habit.positive !== false,
    createdAt: new Date(habit.created_at),
    archived: habit.archived || false,
    habitValue: habit.habit_value || 0,
    dailyCompletions: habit.daily_completions || 0,
    colorState: 'green' as const, // Default color state
  }));
};

const transformSupabaseDailiesToLocal = (supabaseDailies: SupabaseDaily[]): HabitType[] => {
  return supabaseDailies.map(daily => ({
    id: daily.id,
    userId: daily.user_id,
    name: daily.name,
    description: daily.description || '',
    type: 'daily' as const,
    difficulty: daily.difficulty,
    priority: daily.priority || 'medium',
    points: daily.points || 1,
    streak: daily.streak || 0,
    lastCompleted: daily.last_completed_at ? new Date(daily.last_completed_at) : undefined,
    completedToday: daily.completed_today || false,
    tags: daily.tags || [],
    icon: daily.icon,
    color: daily.color || '#10B981',
    positive: true, // Dailies are positive by default
    createdAt: new Date(daily.created_at),
    archived: false, // Dailies loaded are not archived
    habitValue: 0, // Dailies start at 0
    dailyCompletions: 0, // Reset for dailies
    colorState: 'green' as const, // Default color state
  }));
};

const transformSupabaseTodosToLocal = (supabaseTodos: SupabaseTodo[]): HabitType[] => {
  return supabaseTodos.map(todo => ({
    id: todo.id,
    userId: todo.user_id,
    name: todo.name,
    description: todo.description || '',
    type: 'todo' as const,
    difficulty: todo.difficulty,
    priority: todo.priority || 'medium',
    points: todo.points || 1,
    streak: 0, // Todos don't have streaks
    lastCompleted: undefined, // Todos track completion differently
    completedToday: todo.completed || false,
    dueDate: todo.due_date ? new Date(todo.due_date) : undefined,
    tags: todo.tags || [],
    icon: todo.icon,
    color: todo.color || '#F59E0B',
    positive: true, // Todos are positive by default
    createdAt: new Date(todo.created_at),
    archived: false, // Todos loaded are not archived
    habitValue: 0, // Todos start at 0
    dailyCompletions: 0, // Not applicable for todos
    colorState: 'green' as const, // Default color state
  }));
};